<script setup lang="ts">
import { default as PDataTable } from 'primevue/datatable'
import { default as PColumn } from 'primevue/column'
import { default as PInputText } from 'primevue/inputtext'
import { default as PSelect } from 'primevue/select'
import { default as PInputGroup } from 'primevue/inputgroup'
import { default as PInputGroupAddon } from 'primevue/inputgroupaddon'
import CDashboardClassroomResultsCourseTableModuleScore from '@/presentation/components/dashboard/CDashboardClassroomResultsCourseTableModuleScore.vue'
import CDashboardClassroomResultsCourseTableTaskStatus from '@/presentation/components/dashboard/CDashboardClassroomResultsCourseTableTaskStatus.vue'
import { ref } from 'vue'
import type { DashboardClassroomStudentResult } from '@/core/domain/dashboard/DashboardClassroomStudentResult'
import { FilterMatchMode } from '@primevue/core/api'

// Props
const props = defineProps<{
  dataSource: DashboardClassroomStudentResult[]
  teachersOptions: { label: string; value: string }[]
  isLoading: boolean
}>()

// Data
const filters = ref({
  global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  teacherFullName: { value: null, matchMode: FilterMatchMode.CONTAINS }
})
</script>

<template>
  <p-data-table
    :value="props.dataSource"
    dataKey="identifier"
    class="overflow-x-auto text-sm"
    size="small"
    :loading="isLoading"
    v-model:filters="filters"
    removableSort
    scrollable
    sortMode="multiple"
    paginator
    :rows="20"
    :rowsPerPageOptions="[10, 20, 50, 100]"
    :globalFilterFields="['firstname', 'lastname']"
    stripedRows
    responsiveLayout="stack"
  >
    <!-- Header avec filtres -->
    <template #header>
      <div class="flex flex-col items-center justify-between gap-2 sm:flex-row">
        <p-input-group class="w-auto">
          <p-input-group-addon>
            <i class="pi pi-search" />
          </p-input-group-addon>
          <p-input-text
            v-model="filters['global'].value"
            placeholder="Rechercher un élève"
            class="w-full sm:w-auto"
          />
        </p-input-group>

        <p-select
          v-model="filters['teacherFullName'].value"
          :options="teachersOptions"
          optionLabel="label"
          optionValue="value"
          placeholder="Filtrer par enseignant"
          class="w-full sm:w-auto"
        />
      </div>
    </template>

    <template #empty>Aucune donnée trouvée.</template>
    <template #loading>Chargement des données...</template>

    <!-- Colonnes du tableau -->
    <p-column field="firstname" frozen :sortable="true" header="Prénom" class="w-48">
      <template #body="{ data }">
        <p class="m-0" data-maskable>{{ data.firstname }}</p>
      </template>
    </p-column>

    <p-column field="lastname" frozen :sortable="true" header="Nom" class="w-48">
      <template #body="{ data }">
        <p class="m-0" data-maskable>{{ data.lastname }}</p>
      </template>
    </p-column>

    <p-column field="teacherFullName" :sortable="true" header="Enseignant" class="w-48">
      <template #body="{ data }">
        <p class="m-0" data-maskable>
          <template v-if="data.teacherFirstname && data.teacherLastname">
            {{ data.teacherFirstname }} {{ data.teacherLastname }}
          </template>
          <template v-else>-</template>
        </p>
      </template>
    </p-column>

    <p-column
      field="diagScorePercentRounded"
      :sortable="true"
      header="Score diagnostic"
      class="w-48"
    >
      <template #body="{ data }">
        <CDashboardClassroomResultsCourseTableModuleScore
          :score="data.diagScorePercentRounded"
          :eval-range="data.diagEvalRangeName"
          :quiz-version-number="data.diagQuizVersionNumber"
        />
      </template>
    </p-column>

    <p-column field="learningGrains" header="Sessions" class="w-48">
      <template #body="{ data }">
        <div class="flex flex-wrap gap-2">
          <CDashboardClassroomResultsCourseTableTaskStatus
            v-for="(grain, index) in data.learningGrains"
            :key="grain.learningGrainId"
            :grain="grain"
            :grain-eval-range="grain.postQuizEvalRangeName"
          />
        </div>
      </template>
    </p-column>

    <p-column field="moduleScorePercentRounded" :sortable="true" header="Score module" class="w-48">
      <template #body="{ data }">
        <p
          v-if="data.learningGrains?.some((grain) => grain.postQuizEvalRangeName === null)"
          class="text-surface-800 dark:text-surface-50 m-0 italic"
        >
          En cours
        </p>
        <CDashboardClassroomResultsCourseTableModuleScore
          v-else
          :score="data.moduleScorePercentRounded"
          :eval-range="data.moduleEvalRangeName"
          :progress="
            data.diagnosticModuleVariation > 0
              ? '+'
              : data.diagnosticModuleVariation < 0
                ? '-'
                : '='
          "
        />
      </template>
    </p-column>

    <p-column
      field="diagScorePercentRounded"
      :sortable="true"
      header="Score diagnostic D2"
      class="w-48"
    >
      <template #body="{ data }">
        <CDashboardClassroomResultsCourseTableModuleScore
          :score="data.diagEndScorePercentRounded"
          :eval-range="data.diagEndEvalRangeName"
          :progress="
            data.diagEndScorePercentRounded > data.diagScorePercentRounded
              ? '+'
              : data.diagEndScorePercentRounded < data.diagScorePercentRounded
                ? '-'
                : '='
          "
          :quiz-version-number="data.diagEndQuizVersionNumber"
        />
      </template>
    </p-column>
  </p-data-table>
</template>
